import type { DialogModal } from '../../../types/state'
import { EmojiViewerProps } from '../../components/presentation/Modals/Dialogs/EmojiViewer'
import {
    AGE_GATE_NOTICE,
    AGE_VERIFICATION,
    ANNOUNCE,
    CLOSE_DRAFT_CONFIRMATION,
    EMOJI_VIEWER,
    EMOJIS,
    GALLERY_ALBUM_IMAGE,
    GUEST_LIST,
    HIDE_FLOATING_PLAYER,
    MENU,
    MISS_MFC_ANONYMOUS_CONFIRMATION,
    QUICKBUY,
    REQUIRED_CHANGE,
    RULE_VIOLATION,
    SIGNUP_GATE,
    WHITEBOARD,
} from '../../constants/dialogs'
import { GatedDisplayModes, ModalKind } from '../../constants/enums'
import { locales } from '../../locales/en'
import { IUserImage } from '../../mfcLogic/api/chatImages'
import { MergedPrivateGalleryUser } from '../../mfcLogic/api/privateGallery'
import { authenticated as hasAuthCookies } from '../../mfcLogic/auth'
import { getLens } from '../../mfcLogic/lens'
import { getGuestList } from '../../utilities/guestList'
import { getAppState, getModalState } from '../getState'
import { setModal, setShowModal } from '../internal/modals'
import { resetMenuState } from '../state.menu'

const isAuthGateForced = () => {
    const { forceAuthGate, authenticated } = getAppState()
    return !!forceAuthGate && !authenticated && !hasAuthCookies()
}

const isSignupGateLocked = () =>
    isAuthGateForced() && getModalState().dialog.type === SIGNUP_GATE

export const openSignupGateIfForced = () => {
    if (!isAuthGateForced() || getModalState().dialog.show) return
    openSignupGateDialog()
}

export const closeDialog = (immediate = false) => {
    if (isSignupGateLocked()) return
    // safeTransition(() => {
    resetMenuState()
    setShowModal(ModalKind.Dialog, false, immediate)
    // }, skipTransition)
    // setShowModal(ModalKind.Dialog, false, immediate)
    openSignupGateIfForced()
}

export const openAgeGateDialog = (displayMode?: GatedDisplayModes) => {
    const disableClickThrough =
        !displayMode || displayMode === GatedDisplayModes.hideEverything
            ? false
            : true
    setModal(ModalKind.Dialog, {
        type: AGE_GATE_NOTICE,
        show: true,
        disableBackdropClick: true,
        expanded: true,
        close: disableClickThrough,
    })
}

export const openAgeVerificationDialog = () =>
    setModal(ModalKind.Dialog, {
        type: AGE_VERIFICATION,
        show: true,
        disableBackdropClick: true,
        expanded: true,
        close: false,
    })

export const openSignupGateDialog = () =>
    setModal(ModalKind.Dialog, {
        type: SIGNUP_GATE,
        show: true,
        disableBackdropClick: true,
        expanded: true,
        close: false,
    })

export const openCloseDraftConfirmationDialog = (onDiscard: () => void) =>
    setModal(ModalKind.Dialog, {
        type: CLOSE_DRAFT_CONFIRMATION,
        show: true,
        disableBackdropClick: true,
        expanded: true,
        close: false,
        onDiscard,
    })

export const openMissMFCAnonymousDialog = (
    onConfirm: DialogModal['onConfirm'],
) =>
    setModal(ModalKind.Dialog, {
        type: MISS_MFC_ANONYMOUS_CONFIRMATION,
        show: true,
        disableBackdropClick: true,
        expanded: true,
        close: false,
        onConfirm,
    })

export const openRequiredChangeDialog = () =>
    setModal(ModalKind.Dialog, {
        type: REQUIRED_CHANGE,
        show: true,
        disableBackdropClick: true,
        expanded: true,
        close: false,
    })

export interface EmojiPickerProps {
    onInsertEmoticon(key: string): void
    onInsertImage(imageData: IUserImage): void
    showSearch?: boolean
    fullWindow?: boolean
}

export const openEmojiPicker = (props: EmojiPickerProps) =>
    setModal(ModalKind.Dialog, {
        ...props,
        type: EMOJIS,
        show: true,
        close: false,
    })

export const openHideFloatingPlayerDialog = () =>
    setModal(ModalKind.Dialog, {
        type: HIDE_FLOATING_PLAYER,
        show: true,
        title: locales.settings.extraOptions.dialogTitle,
        close: true,
    })

export const openModelMenuDialog = (
    slug: string,
    props: {
        hideNextPrev?: boolean
        message?: boolean
        noVideo?: boolean
        room?: boolean
    },
    immediate = true,
) => {
    getLens()?.ModelMenu_Click({
        ...(props?.room ? { room: true } : {}),
        ...(props?.message ? { message: true } : {}),
        ...(props?.noVideo ? { noVideo: true } : {}),
    })
    setModal(
        ModalKind.Dialog,
        {
            ...props,
            slug,
            type: MENU,
            show: true,
        },
        { immediate },
    )
}

export const openEmojiViewerDialog = (props: EmojiViewerProps) => {
    const { type, emoji, shortcut } = props
    const title = type === 'e' ? 'View Emoji' : 'View Image'

    const normalSizeSrc = !emoji.includes('photos2/')
        ? emoji.replace(/\.x50(\.gif|\.jpg|\.jpeg)/, '$1')
        : emoji
    setModal(ModalKind.Dialog, {
        type: EMOJI_VIEWER,
        show: true,
        close: true,
        title,
        emoji: normalSizeSrc,
        shortcut,
    })
}

interface GalleryDialogProps {
    currentPosition: number
    fullPage: boolean
    gallery: MergedPrivateGalleryUser
    galleries: Array<MergedPrivateGalleryUser>
    title: string
}

export const openGalleryImageDialog = (props: GalleryDialogProps) =>
    setModal(ModalKind.Dialog, {
        ...props,
        type: GALLERY_ALBUM_IMAGE,
        show: true,
    })

export const openGuestListDialog = () => {
    const guestList = getGuestList()
    const friends = Object.keys(guestList?.friend || {})
    const bookmarks = Object.keys(guestList?.bookmark || {})
    const hasFriends = friends.length > 0
    const hasBookmarks = bookmarks.length > 0
    const totalEntries = new Set([...friends, ...bookmarks]).size
    const title =
        `Add ` +
        (hasFriends ? `Friends ` : '') +
        (hasFriends && hasBookmarks ? `  / ` : '') +
        (hasBookmarks ? ` Bookmarks` : '')
    setModal(ModalKind.Dialog, {
        type: GUEST_LIST,
        show: true,
        wide: totalEntries > 4,
        title,
    })
}

interface QuickBuyDialogProps {
    inChatPurchase?: boolean
}

export const openQuickbuyDialog = (props: QuickBuyDialogProps = {}) => {
    const { inChatPurchase } = props
    getLens()?.Purchase_Click({
        quickBuy: true,
        ...(inChatPurchase ? { fromChat: true } : {}),
    })
    setModal(ModalKind.Dialog, { type: QUICKBUY, show: true })
}

export const openRuleViolationDialog = (props: { message: string }) => {
    const { message } = props
    setModal(ModalKind.Dialog, {
        type: RULE_VIOLATION,
        message: message,
        disableBackdropClick: true,
        expanded: true,
        close: false,
        fullPage: true,
        show: true,
    })
}

export const openAnnounceDialog = (props: { message: string }) => {
    const { message } = props
    setModal(ModalKind.Dialog, {
        type: ANNOUNCE,
        disableBackdropClick: true,
        expanded: true,
        close: false,
        fullPage: true,
        show: true,
        message,
    })
}

export const openWhiteboardDialog = (identifier: number | string) => {
    getLens()?.Whiteboard_Click({
        dialog: true,
    })
    setModal(ModalKind.Dialog, {
        type: WHITEBOARD,
        identifier,
        fullHeight: true,
        show: true,
    })
}
